import type { ReactNode } from "react";

interface PeriodData {
  from: string;
  to: string;
  value: number;
}

interface KpiTooltipPartsProps {
  metricName: string;
  currentPeriod: PeriodData;
  priorPeriod: PeriodData;
  change: string;
  formatValue: (value: number) => string;
}

const parseLocalDate = (dateStr: string): Date => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateStr);
};

const formatDate = (dateStr: string): string =>
  parseLocalDate(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });

/**
 * Builds the title and description for the 3-block KPI tooltip showing
 * current period, prior period, and the percentage change between them.
 *
 * Returns separate title/description so BaseTooltip renders the period header
 * in the bold white title style and the body in the supporting text style.
 */
export const buildKpiTooltipParts = ({
  metricName,
  currentPeriod,
  priorPeriod,
  change,
  formatValue,
}: KpiTooltipPartsProps): { title: ReactNode; description: ReactNode } => ({
  title: `Current period (${formatDate(currentPeriod.from)} - ${formatDate(currentPeriod.to)})`,
  description: (
    <>
      {metricName} = {formatValue(currentPeriod.value)}
      <br />
      <br />
      Prior period ({formatDate(priorPeriod.from)} - {formatDate(priorPeriod.to)})
      <br />
      {metricName} = {formatValue(priorPeriod.value)}
      <br />
      <br />
      Change = {change}
    </>
  ),
});
